import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faUser,
  faFlag,
  faCheckCircle,
  faClock,
  faPlay,
  faTimes,
  faTasks,
  faComments,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';

const TaskList = ({
  tasks,
  loading,
  onTaskClick,
  onStatusUpdate,
  onTaskUpdate,
  getStatusColor,
  getPriorityColor
}) => {
  const navigate = useNavigate();
  
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
    if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
    
    return date.toLocaleDateString();
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'completed' || status === 'cancelled') return false;
    return new Date(dueDate) < new Date();
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: faClock,
      todo: faClock,
      in_progress: faPlay,
      completed: faCheckCircle,
      cancelled: faTimes,
      timeout: faTimes
    };
    return icons[status] || faClock;
  };

  const getPriorityIcon = (priority) => {
    return faFlag;
  };

  const handleQuickStatusUpdate = (e, taskId, newStatus) => {
    e.stopPropagation();
    onStatusUpdate(taskId, newStatus);
  };

  const handleCancelTask = async (e, task) => {
    e.stopPropagation();

    // Confirm cancellation
    if (!window.confirm('Are you sure you want to cancel this task? This will also cancel the workflow and mark the chat thread.')) {
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';

      // Get run_id and session_id from task_data
      const taskData = typeof task.task_data === 'string'
        ? JSON.parse(task.task_data)
        : task.task_data;

      const runId = taskData?.run_id;
      const sessionId = taskData?.session_id;

      if (!runId) {
        console.error('No run_id found in task data');
        // Fallback to just updating task status
        onStatusUpdate(task.id, 'cancelled');
        return;
      }

      // Get token from cookies
      const token = Cookies.get('sankalvax_token');
      if (!token) {
        console.error('No authentication token found');
        // Fallback to just updating task status
        onStatusUpdate(task.id, 'cancelled');
        return;
      }

      // Call the cancel-workflow endpoint
      const response = await fetch(`${API_BASE_URL}/api/chat/cancel-workflow`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          run_id: runId,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Task and workflow cancelled:', result);

        // Refresh the task list by calling the status update
        // This will trigger a re-fetch in the parent component
        onStatusUpdate(task.id, 'cancelled');
      } else {
        console.error('Failed to cancel workflow');
        // Still update task status even if API call failed
        onStatusUpdate(task.id, 'cancelled');
      }
    } catch (error) {
      console.error('Error cancelling task:', error);
      // Fallback to just updating task status
      onStatusUpdate(task.id, 'cancelled');
    }
  };

  const handleGoToChat = (e, task) => {
    e.stopPropagation();
    try {
      // Parse task_data to get session_id, workflow_id
      const taskData = typeof task.task_data === 'string'
        ? JSON.parse(task.task_data)
        : task.task_data;

      if (taskData) {
        const { session_id, workflow_id, workflow_name, agent_type, run_id } = taskData;

        // Handle NetSuite Sales Order Agent tasks
        if (agent_type === 'sale_order' && run_id) {
          navigate(`/sale-order-agent?runId=${run_id}`);
          return;
        }

        // Handle Bill/AP Agent tasks (existing logic - unchanged)
        if (session_id) {
          if (workflow_id) {
            // Navigate to workflow chat with workflow context (V2 by default)
            navigate(`/chat/${session_id}?workflow=${workflow_id}&workflowName=${encodeURIComponent(workflow_name || 'Workflow')}&mode=workflow&version=v2`);
          } else {
            // Navigate to regular chat
            navigate(`/chat/${session_id}`);
          }
        }
      }
    } catch (error) {
      console.error('Error navigating to chat:', error);
    }
  };

  const handleGoToNetSuiteBill = (e, task) => {
    e.stopPropagation();
    try {
      // Parse completion_data to get NetSuite bill URL
      const completionData = typeof task.completion_data === 'string'
        ? JSON.parse(task.completion_data)
        : task.completion_data;

      if (completionData && completionData.netsuite_bill_url) {
        // Open NetSuite bill in new tab
        window.open(completionData.netsuite_bill_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error opening NetSuite bill:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="ml-3 text-gray-600 dark:text-gray-400">Loading tasks...</p>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="empty-state">
        <FontAwesomeIcon icon={faTasks} className="empty-state-icon" />
        <h3 className="empty-state-title">No tasks found</h3>
        <p className="empty-state-description">
          Create your first task to get started with task management.
        </p>
      </div>
    );
  }

  return (
    <div className="task-list-container">
      <div className="space-y-3">
        {tasks.map((task, index) => (
          <motion.div
            key={task.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`task-item ${isOverdue(task.due_date, task.status) ? 'border-red-300 dark:border-red-700' : ''}`}
            onClick={() => onTaskClick(task)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {/* Task Title */}
                <h3 className="task-title">
                  {task.title}
                  {isOverdue(task.due_date, task.status) && (
                    <span className="ml-2 text-red-500 text-sm">
                      (Overdue)
                    </span>
                  )}
                </h3>
                
                {/* Task Description */}
                {task.description && (
                  <p className="task-description">
                    {task.description}
                  </p>
                )}
                
                {/* Task Meta Information */}
                <div className="task-meta">
                  <div className="task-badges">
                    {/* Status Badge */}
                    <span className={`task-badge ${getStatusColor(task.status)}`}>
                      <FontAwesomeIcon 
                        icon={getStatusIcon(task.status)} 
                        className="mr-1" 
                      />
                      {task.status.replace('_', ' ').toUpperCase()}
                    </span>
                    
                    {/* Priority Badge */}
                    <span className={`task-badge priority-${task.priority}`}>
                      <FontAwesomeIcon 
                        icon={getPriorityIcon(task.priority)} 
                        className="mr-1" 
                      />
                      {task.priority.toUpperCase()}
                    </span>
                    
                    {/* Assignee Badge */}
                    {task.assignee_name && (
                      <span className="task-badge bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        <FontAwesomeIcon icon={faUser} className="mr-1" />
                        {task.assignee_name}
                      </span>
                    )}
                    
                    {/* Due Date Badge */}
                    {task.due_date && (
                      <span className={`task-badge ${
                        isOverdue(task.due_date, task.status) 
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        <FontAwesomeIcon icon={faCalendar} className="mr-1" />
                        {formatDate(task.due_date)}
                      </span>
                    )}
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="task-actions">
                    {/* Go to Chat Button - Show for approval tasks */}
                    {task.task_type === 'approval' && task.task_data && (
                      <button
                        onClick={(e) => handleGoToChat(e, task)}
                        className="task-action-btn bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-3 py-1 rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all shadow-sm hover:shadow-md"
                        title="Go to Chat"
                      >
                        <FontAwesomeIcon icon={faComments} className="mr-1" />
                        <span className="text-xs font-medium">Go to Chat</span>
                      </button>
                    )}

                    {/* Go to NetSuite Bill Button - Show for completed approval tasks with bill URL */}
                    {task.task_type === 'approval' && task.status === 'completed' && (() => {
                      try {
                        const completionData = typeof task.completion_data === 'string'
                          ? JSON.parse(task.completion_data)
                          : task.completion_data;
                        return completionData && completionData.netsuite_bill_url;
                      } catch {
                        return false;
                      }
                    })() && (
                      <button
                        onClick={(e) => handleGoToNetSuiteBill(e, task)}
                        className="task-action-btn bg-gradient-to-r from-green-500 to-teal-600 text-white px-3 py-1 rounded-lg hover:from-green-600 hover:to-teal-700 transition-all shadow-sm hover:shadow-md"
                        title="Go to NetSuite Bill"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                        <span className="text-xs font-medium">Go to NetSuite Bill</span>
                      </button>
                    )}

                    {(task.status === 'pending' || task.status === 'todo') && (
                      <button
                        onClick={(e) => handleQuickStatusUpdate(e, task.id, 'in_progress')}
                        className="task-action-btn text-blue-600 hover:text-blue-700"
                        title="Start Task"
                      >
                        <FontAwesomeIcon icon={faPlay} />
                      </button>
                    )}

                    {task.status === 'in_progress' && (
                      <button
                        onClick={(e) => handleQuickStatusUpdate(e, task.id, 'completed')}
                        className="task-action-btn text-green-600 hover:text-green-700"
                        title="Complete Task"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </button>
                    )}

                    {(task.status === 'pending' || task.status === 'todo' || task.status === 'in_progress') && (
                      <button
                        onClick={(e) => handleCancelTask(e, task)}
                        className="task-action-btn text-red-600 hover:text-red-700"
                        title="Cancel Task & Delete Thread"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Task Footer */}
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <div>
                  Created by {task.creator_name} on{' '}
                  {new Date(task.created_at).toLocaleDateString()}
                </div>
                <div>
                  Updated {new Date(task.updated_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default TaskList;